import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, AlertCircle, Search } from 'lucide-react';
import { dataService, isDemoMode } from '@/services/data-service';
import { useToast } from '@/hooks/use-toast';
import type { DisposalReason } from '@/types/settings';
import { useDisposalReasonsPermissions } from '@/hooks/use-permissions';

export default function DisposalReasonsPage(): React.ReactElement {
  const { toast } = useToast();
  const { canManage: canManageDisposalReasons } = useDisposalReasonsPermissions();
  const [reasons, setReasons] = useState<DisposalReason[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingReason, setEditingReason] = useState<DisposalReason | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    async function loadReasons() {
      try {
        setLoading(true);
        setError(null);
        const data = await dataService.getDisposalReasons();
        setReasons(data);
      } catch (err) {
        console.error('Failed to load disposal reasons:', err);
        setError('Failed to load disposal reasons. Please try again.');
      } finally {
        setLoading(false);
      }
    }
    loadReasons();
  }, []);

  const [formData, setFormData] = useState<Partial<DisposalReason>>({
    code: '',
    description: '',
  });

  const handleOpenDialog = (reason?: DisposalReason) => {
    if (reason) {
      setEditingReason(reason);
      setFormData(reason);
    } else {
      setEditingReason(null);
      setFormData({
        code: '',
        description: '',
      });
    }
    setIsDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editingReason) {
        if (isDemoMode()) {
          setReasons(
            reasons.map((reason) =>
              reason.id === editingReason.id
                ? { ...reason, ...formData, modifiedDate: new Date().toISOString() }
                : reason
            )
          );
        } else {
          const updated = await dataService.updateDisposalReason(editingReason.id, {
            ...formData,
            modifiedDate: new Date().toISOString(),
          } as DisposalReason);
          setReasons(reasons.map((reason) => (reason.id === updated.id ? updated : reason)));
        }
      } else {
        const newReason: DisposalReason = {
          id: String(reasons.length + 1),
          code: formData.code ?? '',
          description: formData.description ?? '',
          createdBy: 'current-user',
          createdDate: new Date().toISOString(),
        };
        
        if (isDemoMode()) {
          setReasons([...reasons, newReason]);
        } else {
          const created = await dataService.createDisposalReason(newReason);
          setReasons([...reasons, created]);
        }
      }
      toast({
        title: 'Success',
        description: editingReason ? 'Disposal reason updated successfully' : 'Disposal reason created successfully',
      });
      setIsDialogOpen(false);
    } catch (err) {
      console.error('Failed to save disposal reason:', err);
      toast({
        title: 'Error',
        description: 'Failed to save disposal reason. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this disposal reason?')) {
      try {
        if (isDemoMode()) {
          setReasons(reasons.filter((reason) => reason.id !== id));
        } else {
          await dataService.deleteDisposalReason(id);
          setReasons(reasons.filter((reason) => reason.id !== id));
        }
        toast({
          title: 'Success',
          description: 'Disposal reason deleted successfully',
        });
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to delete disposal reason. Please try again.';
        toast({
          title: 'Error',
          description: errorMessage,
          variant: 'destructive',
        });
      }
    }
  };

  const filteredReasons = reasons.filter((reason) =>
    reason.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
    reason.description.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderTableContent = () => {
    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={3} className="text-center text-muted-foreground h-96">
            Loading disposal reasons...
          </TableCell>
        </TableRow>
      );
    }
    if (error) {
      return (
        <TableRow>
          <TableCell colSpan={3} className="text-center text-destructive h-96">
            {error}
          </TableCell>
        </TableRow>
      );
    }
    if (filteredReasons.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={3} className="text-center text-muted-foreground h-96">
            No disposal reasons found
          </TableCell>
        </TableRow>
      );
    }
    return filteredReasons.map((reason, index) => (
      <TableRow 
        key={reason.id}
        className={`
          ${index % 2 === 0 ? 'bg-white dark:bg-slate-950' : 'bg-slate-50/50 dark:bg-slate-900/50'}
          hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors border-b border-slate-100 dark:border-slate-800
        `}
      >
        <TableCell className="font-medium">
          <button
            onClick={() => handleOpenDialog(reason)}
            className="text-primary hover:underline focus:outline-none focus:underline"
          >
            {reason.code}
          </button>
        </TableCell>
        <TableCell>{reason.description}</TableCell>
        <TableCell className="text-right">
          <div className="flex items-center justify-end gap-2">
            {canManageDisposalReasons && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleOpenDialog(reason)}
                title="Edit"
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
            {canManageDisposalReasons && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => handleDelete(reason.id)}
                className="text-destructive hover:text-destructive"
                title="Delete"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </TableCell>
      </TableRow>
    ));
  };

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b bg-card p-6 flex-shrink-0">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Disposal Reasons</h1>
              <p className="text-sm text-muted-foreground mt-1">
                Manage reasons for asset disposal
              </p>
            </div>
          </div>
          
          {/* Search Bar and Add Button - Right aligned */}
          <div className="flex items-center gap-3">
            <div className="relative w-80">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search disposal reasons..."
                value={searchQuery}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            {canManageDisposalReasons && (
              <Button onClick={() => handleOpenDialog()}>
                <Plus className="mr-2 h-4 w-4" />
                Add Reason
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Table Container - Fills remaining space */}
      <div className="flex-1 overflow-hidden flex flex-col min-h-0">
        <div className="flex-1 overflow-auto">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-slate-50 dark:bg-slate-900">
              <TableRow className="border-b-2 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800">
                <TableHead className="h-12 font-semibold">Code</TableHead>
                <TableHead className="h-12 font-semibold">Description</TableHead>
                <TableHead className="h-12 font-semibold text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {renderTableContent()}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle>
              {editingReason ? 'Edit Disposal Reason' : 'Add Disposal Reason'}
            </DialogTitle>
            <DialogDescription>
              {editingReason
                ? 'Update the disposal reason details below.'
                : 'Enter the details for the new disposal reason.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code">Code *</Label>
              <Input
                id="code"
                value={formData.code}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  setFormData({ ...formData, code: e.target.value.toUpperCase() })
                }
                placeholder="e.g., SOLD"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description *</Label>
              <Input
                id="description"
                value={formData.description}
                onChange={(e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                placeholder="e.g., Asset sold to third party"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!formData.code || !formData.description}
            >
              {editingReason ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
